// ============================================================================
// Wallet ledger — the FULL history of a client's wallet on the client-details
// page. Ops opens this to see how the current balance was arrived at across
// many cancellations and top-ups: each row is a WalletTransaction (credit)
// with its own spent / available / earmarked / refunded breakdown.
//
// Always expanded; the top-right chip in the page header is a link that
// smooth-scrolls to this section via #wallet-ledger.
// ============================================================================
import { Button, Empty, Input, InputNumber, message, Modal, Select, Spin, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  admitCnToWallet,
  fetchWalletLedger,
  WalletLedgerEntry,
  walletRefundToCustomer,
} from "../../apis/pro_forma_invoice.api";
import {
  UserBankDetails, listUserBankDetails,
} from "../oms/returns/returns.api";
import { AddBankAccountModal } from "../oms/returns/components/refund_modal";

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 2,
});

// WalletTransaction money fields (amount / available_balance / to_be_refunded_amount /
// refunded_amount) are stored in PAISE — the whole wallet + EPT surface uses paise. This
// component displays rupees, so convert at the render boundary.
const paiseToRupees = (v: string | number): number => Number(v || 0) / 100;

const SOURCE_LABEL: Record<WalletLedgerEntry["source"], string> = {
  PG_TOPUP: "Top-up",
  ZOHO_CN: "Refund CN",
  CASH: "Cash",
};

const SOURCE_COLOR: Record<WalletLedgerEntry["source"], string> = {
  PG_TOPUP: "blue",
  ZOHO_CN: "green",
  CASH: "gold",
};

const STATUS_COLOR: Record<WalletLedgerEntry["status"], string> = {
  SUCCESS: "green",
  INITIATED: "gold",
  FAILED: "red",
};

// Compact monospace amount cell — negative amounts render red so a row's "spent" or
// "refunded" chunk reads as money going OUT at a glance.
const amountCell = (
  value: string | number,
  { negative = false }: { negative?: boolean } = {}
) => {
  const n = paiseToRupees(value);
  if (!n) return <span style={{ color: "#bbb" }}>—</span>;
  return (
    <span style={{ fontFamily: "monospace", color: negative ? "#cf1322" : "inherit" }}>
      {negative ? `-${inr.format(n)}` : inr.format(n)}
    </span>
  );
};

// Reference the row was minted against — the Zoho SO for a CN refund, the payment id for a
// top-up. Clickable for a SO so support can jump straight to the order that produced the credit.
const referenceCell = (row: WalletLedgerEntry) => {
  if (row.sales_order) {
    const label = row.sales_order.zoho_salesorder_number || `SO #${row.sales_order.id}`;
    return (
      <Link to={`/oms/${row.sales_order.id}`} target="_blank" rel="noopener noreferrer">
        {label}
      </Link>
    );
  }
  if (row.payment_id) {
    return (
      <Typography.Text copyable style={{ fontFamily: "monospace", fontSize: 12 }}>
        {row.payment_id}
      </Typography.Text>
    );
  }
  return <span style={{ color: "#bbb" }}>—</span>;
};

// `onRefundClick` is passed in from the component so each row's "Refund to customer" button can
// open the same modal (one state owner, one submit path). Rows with 0 spendable balance render
// nothing in that cell — nothing to earmark.
const buildColumns = (
  onRefundClick: (row: WalletLedgerEntry) => void
): ColumnsType<WalletLedgerEntry> => [
  {
    title: "Date",
    dataIndex: "created_at",
    width: 140,
    render: (v: string) => (v ? dayjs(v).format("DD MMM YYYY, HH:mm") : "—"),
  },
  {
    title: "Source",
    dataIndex: "source",
    width: 100,
    render: (v: WalletLedgerEntry["source"]) => (
      <Tag color={SOURCE_COLOR[v]} style={{ margin: 0 }}>{SOURCE_LABEL[v]}</Tag>
    ),
  },
  {
    title: "Reference",
    key: "reference",
    render: (_, row) => referenceCell(row),
  },
  {
    title: "Credited",
    dataIndex: "amount",
    align: "right",
    width: 130,
    render: (v) => amountCell(v),
  },
  {
    title: "Spent",
    dataIndex: "spent_amount",
    align: "right",
    width: 130,
    render: (v) => amountCell(v, { negative: true }),
  },
  {
    title: "To be refunded",
    dataIndex: "to_be_refunded_amount",
    align: "right",
    width: 140,
    render: (v) => amountCell(v, { negative: true }),
  },
  {
    title: "Refunded",
    dataIndex: "refunded_amount",
    align: "right",
    width: 130,
    render: (v) => amountCell(v, { negative: true }),
  },
  {
    title: "Available",
    dataIndex: "available_balance",
    align: "right",
    width: 130,
    render: (v) => {
      const n = paiseToRupees(v);
      return (
        <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#389e0d" }}>
          {n ? inr.format(n) : <span style={{ color: "#bbb" }}>—</span>}
        </span>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    width: 100,
    render: (v: WalletLedgerEntry["status"]) => (
      <Tag color={STATUS_COLOR[v]} style={{ margin: 0 }}>{v.toLowerCase()}</Tag>
    ),
  },
  {
    title: "Action",
    key: "action",
    width: 160,
    render: (_, row) =>
      paiseToRupees(row.available_balance) > 0 ? (
        <Button size="small" type="link" onClick={() => onRefundClick(row)}>
          Refund to customer
        </Button>
      ) : (
        <span style={{ color: "#bbb" }}>—</span>
      ),
  },
];

const WalletLedgerSection: React.FC<{ clientId?: string | number }> = ({ clientId }) => {
  const [rows, setRows] = useState<WalletLedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);

  // Admit-CN modal: local state kept close to the button since nothing else on the page needs it.
  const [admitOpen, setAdmitOpen] = useState(false);
  const [cnInput, setCnInput] = useState("");
  const [admitError, setAdmitError] = useState<string | null>(null);
  const [admitting, setAdmitting] = useState(false);

  // Row-level "Refund to customer" modal — one row at a time; opened via the Action-column button.
  // `refundingRow` is the ledger row being acted on (null = modal closed); `refundRupees` is the
  // operator-editable amount (rupees — converted to paise at the API boundary); `bankDetailsId`
  // is the customer's bank account the earmark will route the eventual payout to.
  const [refundingRow, setRefundingRow] = useState<WalletLedgerEntry | null>(null);
  const [refundRupees, setRefundRupees] = useState<number>(0);
  const [refundNote, setRefundNote] = useState("");
  const [banks, setBanks] = useState<UserBankDetails[]>([]);
  const [bankDetailsId, setBankDetailsId] = useState<number | null>(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [addBankOpen, setAddBankOpen] = useState(false);

  const loadLedger = React.useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    fetchWalletLedger(clientId).then((res) => {
      setRows(res?.transactions ?? []);
      setLoading(false);
    });
  }, [clientId]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const handleAdmit = async () => {
    const cnId = cnInput.trim();
    // Enter can fire while a request is in flight (the OK button is disabled, the input is not).
    if (!cnId || !clientId || admitting) return;
    setAdmitting(true);
    setAdmitError(null);
    try {
      await admitCnToWallet(clientId, cnId);
      message.success(`CN ${cnId} credited to wallet.`);
      setAdmitOpen(false);
      setCnInput("");
      loadLedger();
    } catch (err: any) {
      // Backend gives the safe user-visible reason on `error`; fall back to a generic string
      // only when the request never reached the app (network error, 5xx without body).
      const backendMsg = err?.response?.data?.error;
      setAdmitError(backendMsg || "Could not admit CN. Please try again.");
    } finally {
      setAdmitting(false);
    }
  };

  // Balance summary — sum across all rows so the header shows the same figure the chip does,
  // derived from the same source of truth the operator is looking at underneath.
  const availableTotal = rows.reduce((s, r) => s + paiseToRupees(r.available_balance), 0);
  const toBeRefundedTotal = rows.reduce((s, r) => s + paiseToRupees(r.to_be_refunded_amount), 0);

  const openRefundRow = (row: WalletLedgerEntry) => {
    setRefundingRow(row);
    // Default amount = full spendable balance on the row; operator can edit down for a partial.
    setRefundRupees(paiseToRupees(row.available_balance));
    setRefundNote("");
    setBankDetailsId(null);
    setRefundError(null);
    if (clientId) listUserBankDetails(String(clientId)).then(setBanks);
  };

  const closeRefundRow = () => {
    setRefundingRow(null);
    setRefundError(null);
  };

  const handleRefundSubmit = async () => {
    if (!refundingRow) return;
    const availableRupees = paiseToRupees(refundingRow.available_balance);
    if (!refundRupees || refundRupees <= 0) {
      setRefundError("Enter an amount greater than zero.");
      return;
    }
    if (refundRupees > availableRupees) {
      setRefundError(
        `Amount exceeds this row's available balance (${inr.format(availableRupees)}).`
      );
      return;
    }
    if (!bankDetailsId) {
      setRefundError("Pick the customer's bank account for the eventual payout.");
      return;
    }
    setRefundSubmitting(true);
    setRefundError(null);
    try {
      // The ledger stores paise; convert at the boundary. `Math.round` guards against the usual
      // float trail (e.g. 150.00000000000003 after a user types 150).
      const amount_paise = Math.round(refundRupees * 100);
      await walletRefundToCustomer(refundingRow.id, {
        amount_paise,
        bank_details_id: bankDetailsId,
        note: refundNote,
      });
      message.success(
        `Earmarked ${inr.format(refundRupees)} on this row for customer-bank payout.`
      );
      closeRefundRow();
      loadLedger();
    } catch (err: any) {
      const backendMsg = err?.response?.data?.detail;
      setRefundError(backendMsg || "Could not earmark this refund. Please try again.");
    } finally {
      setRefundSubmitting(false);
    }
  };

  return (
    <div id="wallet-ledger" style={{ scrollMarginTop: 80 }}>
      <div className="pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="align-middle th-font-size-18 th-font-weight-600 primary-color-text">
          Wallet ledger ({rows.length} {rows.length === 1 ? "entry" : "entries"})
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Tag color="green" style={{ margin: 0, fontSize: 13, padding: "2px 10px" }}>
            Available: <b>{inr.format(availableTotal)}</b>
          </Tag>
          {toBeRefundedTotal > 0 && (
            <Tag color="gold" style={{ margin: 0, fontSize: 13, padding: "2px 10px" }}>
              To be refunded: <b>{inr.format(toBeRefundedTotal)}</b>
            </Tag>
          )}
          <Button size="small" onClick={() => setAdmitOpen(true)} disabled={!clientId}>
            Admit CN to wallet
          </Button>
        </div>
      </div>
      <Modal
        title="Admit Zoho credit note to wallet"
        open={admitOpen}
        onCancel={() => {
          setAdmitOpen(false);
          setAdmitError(null);
          setCnInput("");
        }}
        onOk={handleAdmit}
        okText="Admit"
        okButtonProps={{ disabled: !cnInput.trim(), loading: admitting }}
        confirmLoading={admitting}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Paste the Zoho credit note id. We'll check the CN belongs to this client and hasn't
          already been added, then credit its unapplied balance to their wallet.
        </Typography.Paragraph>
        <Input
          placeholder="e.g. 1234567890000012345"
          value={cnInput}
          onChange={(e) => {
            setCnInput(e.target.value);
            if (admitError) setAdmitError(null);
          }}
          onPressEnter={handleAdmit}
          autoFocus
        />
        {admitError && (
          <Typography.Text type="danger" style={{ display: "block", marginTop: 8 }}>
            {admitError}
          </Typography.Text>
        )}
      </Modal>
      {loading ? (
        <Spin style={{ display: "block", padding: 24 }} />
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No wallet activity yet for this client"
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          columns={buildColumns(openRefundRow)}
          dataSource={rows}
          scroll={{ x: 1260 }}
        />
      )}

      <Modal
        title="Refund wallet balance to customer"
        open={!!refundingRow}
        onCancel={refundSubmitting ? undefined : closeRefundRow}
        onOk={handleRefundSubmit}
        okText="Earmark for payout"
        okButtonProps={{ loading: refundSubmitting }}
        confirmLoading={refundSubmitting}
        destroyOnClose
      >
        {refundingRow && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Earmarking money off this row's spendable balance into "To be refunded". The Cash Log
              Refunds tab will read the earmark + chosen bank account when ops executes the actual
              transfer. A second refund on the same row adds to the existing earmark rather than
              replacing it.
            </Typography.Paragraph>
            <div style={{ display: "flex", gap: 24, fontSize: 13 }}>
              <div>
                Available on row:{" "}
                <b>{inr.format(paiseToRupees(refundingRow.available_balance))}</b>
              </div>
              {paiseToRupees(refundingRow.to_be_refunded_amount) > 0 && (
                <div>
                  Already earmarked:{" "}
                  <b>{inr.format(paiseToRupees(refundingRow.to_be_refunded_amount))}</b>
                </div>
              )}
            </div>
            <div>
              <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
                Amount (₹)
              </Typography.Text>
              <InputNumber
                style={{ width: "100%" }}
                min={0.01}
                max={paiseToRupees(refundingRow.available_balance)}
                step={0.01}
                precision={2}
                value={refundRupees}
                onChange={(v) => setRefundRupees(Number(v) || 0)}
                autoFocus
              />
            </div>
            <div>
              <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
                Bank account
              </Typography.Text>
              <Select
                style={{ width: "100%" }}
                value={bankDetailsId ?? undefined}
                onChange={(v) => setBankDetailsId(Number(v))}
                placeholder="Select the customer's bank account"
                options={banks.map((b) => ({
                  value: b.id,
                  label: `${b.bank_name} — ${b.account_holder_name} (…${b.account_number.slice(-4)})`,
                }))}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div style={{ padding: 8, borderTop: "1px solid #f0f0f0" }}>
                      <Button type="link" size="small"
                              onClick={() => setAddBankOpen(true)}>+ Add new bank account</Button>
                    </div>
                  </>
                )}
              />
            </div>
            <div>
              <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
                Note (optional)
              </Typography.Text>
              <Input.TextArea
                rows={2}
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value)}
                placeholder="Short context for the audit trail."
              />
            </div>
            {refundError && (
              <Typography.Text type="danger">{refundError}</Typography.Text>
            )}
          </div>
        )}
      </Modal>

      <AddBankAccountModal
        open={addBankOpen}
        onClose={() => setAddBankOpen(false)}
        userId={clientId ? String(clientId) : undefined}
        onAdded={(row) => {
          setBanks((prev) => [row, ...prev.filter((b) => b.id !== row.id)]);
          setBankDetailsId(row.id);
          setAddBankOpen(false);
        }}
      />
    </div>
  );
};

export default WalletLedgerSection;
